import React from 'react';
import { Reply, Smile, Check } from 'lucide-react';
import { Message, OnlineUser, handleAvatarError } from '../../types';
import { resolveAvatarUrl } from '../../utils/serverUrl';
import { PresenceDot } from '../PresenceDot';
import { formatDateLabel } from '../../utils/date';
import { getEmojiOnlyLevel } from '../../utils/emoji';
import { LinkPreviewCard } from './LinkPreviewCard';
import { CalendarEventCard } from './CalendarEventCard';

// 메시지 행과 컨텍스트 메뉴가 공유하는 리액션 목록
export const REACTION_EMOJIS = ['✅', '👍', '👎', '❤️', '😂', '😮', '👏'];

/** 첨부만 보낸 메시지에 저장되는 자리표시 텍스트 (ChatWindow 전송부가 삽입) */
export const ATTACHMENT_ONLY_TEXT = '파일을 공유했습니다.';
/** 자리표시 텍스트를 뺀 '진짜 본문'이 있는지 */
export const hasDisplayText = (text?: string) => !!text && text !== ATTACHMENT_ONLY_TEXT;

/**
 * 이모지만 있는 메시지의 크기 단계. 인덱스가 곧 이모지 개수(1~3)다.
 * ⚠ 클래스명을 문자열로 조립하면 Tailwind JIT가 스캔하지 못해 조용히 사라진다 — 리터럴 유지.
 */
const EMOJI_SIZE_CLASS = ['', 'text-5xl', 'text-4xl', 'text-3xl'] as const;

// Intl 포맷터는 생성 비용이 커서 모듈 레벨 싱글턴으로 재사용
const TIME_FORMATTER = new Intl.DateTimeFormat('ko-KR', { hour: '2-digit', minute: '2-digit' });

export interface MessageItemProps {
  msg: Message;
  isOwn: boolean;
  isDeleted: boolean;
  /** 검색·답장 점프로 잠깐 강조된 상태 */
  isHighlighted: boolean;
  /** 이 메시지 위에 날짜 구분선을 그릴지. 목록 전체를 한 번 훑어 미리 계산한 값 */
  showDateSeparator: boolean;
  /**
   * 같은 사람이 5분 안에 이어 보낸 메시지(그룹핑). true면 아바타·이름·시간 행을 생략하고
   * 말풍선만 이전 메시지 아래에 붙인다. ChatWindow가 목록을 한 번 훑어 계산한 원시 boolean.
   */
  isContinuation: boolean;
  /**
   * 발신자의 접속 상태 — 아바타 우하단 점. undefined면 점을 그리지 않는다
   * (본인 메시지·DM·나와의 채팅). ChatWindow가 채널 타입까지 판정해 넘기므로
   * 여기서는 값의 유무만 본다. 원시 문자열이라 아래 React.memo 계약을 지킨다.
   */
  senderStatus?: OnlineUser['status'];
  darkMode?: boolean;
  /** 공개 채널은 전 직원이 자동 참여라 안읽음 숫자가 의미 없어 배지를 숨긴다 */
  isPublicChannel: boolean;
  currentUserId: string;
  currentUserAvatar?: string;
  selectMode: boolean;
  isSelected: boolean;
  /** 이 메시지가 열린 컨텍스트 메뉴의 대상인지 (말풍선 어둡게) */
  isMenuTarget: boolean;
  isReactionPickerOpen: boolean;
  registerRef: (messageId: number, el: HTMLDivElement | null) => void;
  /**
   * 말풍선 '안'에 들어갈 내용(전달 라벨·답장 인용·텍스트) 렌더러.
   * 멘션/링크/검색 하이라이트가 ChatWindow의 여러 state에 얽혀 있어 여기로 옮기지 않고
   * 안정된 콜백으로 주입받는다. 이 참조가 흔들리면 memo가 통째로 무력화되므로 반드시 useCallback.
   */
  renderBubbleContent: (msg: Message, isOwn: boolean) => React.ReactNode;
  /**
   * 말풍선 '밖'에 그리는 첨부(이미지 그리드 + 파일 카드) 렌더러. 위와 동일한 useCallback 계약.
   * 좌/우 정렬은 아래 스택 래퍼가 하므로 isOwn을 넘기지 않는다.
   */
  renderAttachmentBlock: (msg: Message) => React.ReactNode;
  onToggleSelect: (msg: Message) => void;
  onReply: (msg: Message) => void;
  onReactionToggle?: (messageId: number, emoji: string) => void;
  onReactionPickerToggle: (messageId: number) => void;
  // 롱프레스·스와이프 답장·우클릭 메뉴는 여러 ref를 공유하는 한 덩어리라 ChatWindow에 남기고
  // 이벤트만 위임한다. 아래 콜백들도 전부 useCallback이어야 한다.
  onTouchStart: (msg: Message, isOwn: boolean, e: React.TouchEvent<HTMLDivElement>) => void;
  onTouchMove: (msg: Message, isOwn: boolean, e: React.TouchEvent<HTMLDivElement>) => void;
  onTouchEnd: (msg: Message, e: React.TouchEvent<HTMLDivElement>) => void;
  onTouchCancel: () => void;
  onContextMenu: (msg: Message, isOwn: boolean, e: React.MouseEvent<HTMLDivElement>) => void;
  /**
   * 아바타 클릭 → 발신자 프로필 모달. ChatWindow가 타인 메시지에만 넘긴다
   * (나와의 채팅·내 메시지·영상통화 사이드바는 undefined).
   * 미전달이면 예전처럼 장식용 이미지다. 역시 useCallback 고정 참조여야 한다.
   */
  onAvatarClick?: (msg: Message) => void;
}

const MessageItemComponent: React.FC<MessageItemProps> = ({
  msg,
  isOwn,
  isDeleted,
  isHighlighted,
  showDateSeparator,
  isContinuation,
  senderStatus,
  darkMode,
  isPublicChannel,
  currentUserId,
  currentUserAvatar,
  selectMode,
  isSelected,
  isMenuTarget,
  isReactionPickerOpen,
  registerRef,
  renderBubbleContent,
  renderAttachmentBlock,
  onToggleSelect,
  onReply,
  onReactionToggle,
  onReactionPickerToggle,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onTouchCancel,
  onContextMenu,
  onAvatarClick,
}) => {
  const dateSeparator = showDateSeparator ? (
    <div className="flex items-center gap-3 py-2">
      <div className={`flex-1 border-t ${darkMode ? 'border-gray-700' : 'border-slate-300'}`} />
      <span className={`text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap ${darkMode ? 'text-gray-400 bg-gray-800' : 'text-slate-500 bg-slate-50'}`}>
        {formatDateLabel(msg.timestamp)}
      </span>
      <div className={`flex-1 border-t ${darkMode ? 'border-gray-700' : 'border-slate-300'}`} />
    </div>
  ) : null;

  // 시스템 메시지 렌더링
  if (msg.msgType === 'system') {
    return (
      <>
        {dateSeparator}
        <div
          ref={(el) => registerRef(msg.id, el)}
          className="flex items-center gap-3 py-1 mt-3 md:mt-5 first:mt-0"
        >
          <div className={`flex-1 border-t ${darkMode ? 'border-gray-700' : 'border-slate-200'}`} />
          <span className={`text-xs text-center ${darkMode ? 'text-gray-500' : 'text-slate-400'}`}>
            {msg.text}
          </span>
          <div className={`flex-1 border-t ${darkMode ? 'border-gray-700' : 'border-slate-200'}`} />
        </div>
      </>
    );
  }

  const hasAttachments = !!msg.attachments && msg.attachments.length > 0;

  // 이모지만 있는 짧은 메시지는 말풍선 없이 크게 (카카오톡·슬랙 방식).
  // 전달 라벨·답장 인용은 renderBubbleContent 안에서 그려지므로 말풍선을 없애면 허공에 뜬다.
  // 그래서 순수 이모지 메시지에만 적용한다. msg는 이미 prop이라 새 prop 없이 파생한다(memo 계약 유지).
  const emojiLevel = isDeleted || msg.replyToId || msg.forwardedFrom || hasAttachments
    ? 0
    : getEmojiOnlyLevel(msg.text);

  // 첨부는 말풍선 없이 콘텐츠 그대로 그린다(카카오톡 방식). 말풍선은 텍스트성 내용만 감싼다.
  // 삭제된 메시지는 첨부를 그리지 않고 안내 문구만 남긴다(현행 유지).
  const showAttachments = !isDeleted && hasAttachments;
  // 일정 공유 카드는 말풍선 자리에 그린다 — 본문은 구버전 클라이언트·목록 미리보기용 대체 텍스트라 숨긴다.
  // 전달 라벨은 카드가 단다. msg에서 파생하므로 새 prop이 없다(memo 계약 유지)
  const calendarCard = !isDeleted && msg.calendarEvent ? msg.calendarEvent : null;
  const showBubble = !calendarCard && (isDeleted || !hasAttachments
    || hasDisplayText(msg.text) || !!msg.replyToId || !!msg.forwardedFrom);

  // 아바타 이미지 + 접속 점. 아래에서 button(프로필 열기) 또는 span으로 감싼다.
  // 래퍼는 shrink-0을 갖고, img에는 block을 줘서 inline 요소의 baseline 여백이 점 위치를 흔들지 않게 한다.
  // ⚠ 래퍼의 self-start가 없으면 행의 기본 align-items:stretch로 래퍼가 말풍선 높이만큼 늘어나
  //   -bottom-0.5로 붙인 점이 아바타가 아니라 행 맨 아래로 내려간다.
  const avatarContent = (
    <>
      <img
        src={resolveAvatarUrl(isOwn ? currentUserAvatar : msg.senderAvatar)} onError={handleAvatarError}
        alt={msg.senderName}
        loading="lazy"
        decoding="async"
        className={`h-10 w-10 rounded-full object-cover border border-slate-200 block ${onAvatarClick ? 'transition-opacity hover:opacity-80' : ''}`}
      />
      {senderStatus && (
        // 테두리 색이 darkMode 분기인 이유: 이 컴포넌트의 유일한 darkMode={true} 사용처가
        // 영상통화 채팅 사이드바라, 거기서 border-white를 쓰면 점 둘레에 흰 링이 뜬다.
        <PresenceDot
          status={senderStatus}
          className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 border-2 ${
            darkMode ? 'border-chrome-line' : 'border-white'
          }`}
        />
      )}
    </>
  );

  return (
    <>
      {dateSeparator}
      <div
        ref={(el) => registerRef(msg.id, el)}
        className={`relative flex gap-2 md:gap-4 group touch-callout-none ${isContinuation ? '' : 'mt-3 md:mt-5 first:mt-0'} ${isOwn ? 'flex-row-reverse' : ''} ${isHighlighted ? 'bg-yellow-100 -mx-4 px-4 py-2 rounded-lg transition-colors duration-300' : ''
          } ${selectMode && isSelected ? (darkMode ? 'bg-brand-900/40' : 'bg-brand-100') + ' rounded-lg' : ''}`}
        // pan-y: 수평 드래그를 브라우저 스크롤로 뺏기지 않고 touchmove로 계속 수신 (스와이프 답장용).
        // React 루트 리스너가 passive라 touchmove에서 preventDefault가 불가한 문제의 정공법
        style={{ touchAction: 'pan-y' }}
        onTouchStart={(e) => onTouchStart(msg, isOwn, e)}
        onTouchMove={(e) => onTouchMove(msg, isOwn, e)}
        onTouchEnd={(e) => onTouchEnd(msg, e)}
        onTouchCancel={onTouchCancel}
        onContextMenu={(e) => onContextMenu(msg, isOwn, e)}
      >
        {/* 선택 모드 오버레이: 행 전체를 덮어 자식 인터랙션(링크·이미지·버튼)을 차단하고 탭=토글 */}
        {selectMode && (
          <button
            type="button"
            onClick={() => onToggleSelect(msg)}
            className={`absolute inset-0 z-10 flex items-center px-1 ${isDeleted ? 'cursor-default' : ''}`}
            aria-label="메시지 선택"
          >
            {!isDeleted && (
              <span
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                  isSelected
                    ? 'bg-brand-600 border-brand-600 text-white'
                    : 'border-slate-300 bg-white/80 text-transparent'
                }`}
              >
                <Check size={12} strokeWidth={3} />
              </span>
            )}
          </button>
        )}
        {isContinuation ? (
          // 그룹핑된 후속 메시지: 아바타 자리만 비워 말풍선 좌/우 정렬을 첫 메시지와 맞춘다
          <div className="w-10 shrink-0" aria-hidden="true" />
        ) : msg.senderName === '관리자' ? (
          <div className="h-10 w-10 rounded-full bg-brand-600 flex items-center justify-center border border-brand-700 shrink-0">
            <span className="text-white text-xs font-bold">관리자</span>
          </div>
        ) : onAvatarClick ? (
          // 누르면 발신자 프로필 모달이 열린다(1:1 대화는 모달의 버튼으로).
          // 짧은 탭만 click이 된다 — 롱프레스 메뉴·스와이프 답장은 행의 touchend가 preventDefault로 클릭을 먹는다.
          // 선택 모드에서는 행 전체를 덮는 z-10 오버레이가 이 버튼보다 위에 있다.
          <button
            type="button"
            onClick={() => onAvatarClick(msg)}
            className="relative shrink-0 self-start rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
            aria-label={`${msg.senderName}님 프로필 보기`}
            title="프로필 보기"
          >
            {avatarContent}
          </button>
        ) : (
          <span className="relative shrink-0 self-start">
            {avatarContent}
          </span>
        )}
        <div className={`flex flex-col max-w-[calc(100%-3.5rem)] md:max-w-[70%] min-w-0 ${isOwn ? 'items-end' : 'items-start'}`}>
          {!isContinuation && (
            <div className="flex items-baseline gap-2 mb-1">
              <span className={`text-sm font-bold ${darkMode ? 'text-gray-200' : 'text-slate-700'}`}>{msg.senderName}</span>
              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-slate-400'}`}>
                {TIME_FORMATTER.format(new Date(msg.timestamp))}
              </span>
              {msg.updatedAt && !isDeleted && (
                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-slate-400'}`}>(수정됨)</span>
              )}
            </div>
          )}

          <div className="flex items-end gap-1.5 relative min-w-0 max-w-full">
            {/* 읽지 않은 수 배지 (카카오톡 스타일). 공개 채널은 전 직원이 자동 참여라
                숫자가 항상 크고 의미가 없어 표시하지 않는다 */}
            {!isDeleted && !isPublicChannel && msg.unreadCount !== undefined && msg.unreadCount > 0 && (
              <span className={`text-xs font-medium text-green-500 mb-1 select-none shrink-0 ${isOwn ? '' : 'order-last'}`}>
                {msg.unreadCount}
              </span>
            )}
            {/* 액션 버튼: 데스크톱 hover엔 답장·리액션만 — 나머지는 우클릭 컨텍스트 메뉴 (모바일은 long-press) */}
            {!isDeleted && (
              <div
                className={`hidden md:flex opacity-0 items-center gap-0.5 md:group-hover:opacity-100 transition-opacity ${isOwn ? 'order-first' : 'order-last'}`}
              >
                <button
                  onClick={() => onReply(msg)}
                  className="p-1.5 text-slate-400 hover:text-brand-600"
                  title="답장"
                >
                  <Reply size={14} />
                </button>
                <div className="relative">
                  <button
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      onReactionPickerToggle(msg.id);
                    }}
                    className="p-1.5 text-slate-400 hover:text-yellow-500"
                    title="리액션"
                  >
                    <Smile size={14} />
                  </button>
                  {isReactionPickerOpen && (
                    <div
                      className={`absolute bottom-full mb-1 ${isOwn ? 'right-0' : 'left-0'} bg-white rounded-lg shadow-lg border border-slate-200 p-1 flex gap-0.5 z-50`}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {REACTION_EMOJIS.map(emoji => (
                        <button
                          key={emoji}
                          onClick={() => {
                            onReactionToggle?.(msg.id, emoji);
                            onReactionPickerToggle(msg.id);
                          }}
                          className="hover:bg-slate-100 rounded p-1 text-lg transition-colors"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 첨부(위) / 말풍선(아래) 2블록 스택.
                brightness-75(컨텍스트 메뉴 대상 강조)는 두 블록에 모두 걸려야 하므로 이 래퍼가 받는다 */}
            <div className={`flex flex-col gap-1 min-w-0 max-w-full ${isOwn ? 'items-end' : 'items-start'} ${isMenuTarget ? 'brightness-75' : ''}`}>
              {showAttachments && renderAttachmentBlock(msg)}
              {calendarCard && (
                <CalendarEventCard
                  card={calendarCard}
                  messageId={msg.id}
                  darkMode={darkMode}
                  forwardedFrom={msg.forwardedFrom?.senderName || null}
                />
              )}
              {showBubble && (
                <div
                  className={`max-w-full min-w-0 ${
                    emojiLevel > 0
                      ? `px-1 py-0.5 leading-none ${EMOJI_SIZE_CLASS[emojiLevel]}`
                      : isDeleted
                        ? `rounded-2xl text-sm leading-relaxed shadow-sm px-4 py-2 italic ${darkMode ? 'bg-gray-800 text-gray-500 border border-gray-700' : 'bg-slate-100 text-slate-400 border border-slate-200'}`
                        // 각진 위쪽 모서리(꼬리)는 아바타를 향할 때만 의미가 있다. 위에 첨부가 오면 사방을 둥글게
                        : showAttachments
                          ? `rounded-2xl text-sm leading-relaxed shadow-sm px-4 py-2 ${isOwn ? 'bg-brand-200 text-slate-800' : darkMode ? 'bg-gray-800 text-gray-200 border border-gray-700' : 'bg-white text-slate-700 border border-slate-200'}`
                          : `rounded-2xl text-sm leading-relaxed shadow-sm px-4 py-2 ${isOwn ? 'bg-brand-200 text-slate-800 rounded-tr-none' : darkMode ? 'bg-gray-800 text-gray-200 border border-gray-700 rounded-tl-none' : 'bg-white text-slate-700 border border-slate-200 rounded-tl-none'}`
                  }`}
                >
                  {isDeleted ? '(삭제된 메시지입니다)' : renderBubbleContent(msg, isOwn)}
                </div>
              )}
              {/* 링크 미리보기 카드 — 말풍선 '밖' 아래.
                  회수한 메시지에 카드가 남지 않도록 isDeleted를 반드시 본다. */}
              {!isDeleted && msg.linkPreview && (
                <LinkPreviewCard preview={msg.linkPreview} darkMode={darkMode} />
              )}
            </div>
          </div>

          {/* 리액션 배지 표시 */}
          {msg.reactions && msg.reactions.length > 0 && (
            <div className={`flex flex-wrap gap-1.5 mt-1.5 ${isOwn ? 'justify-end' : 'justify-start'}`}>
              {msg.reactions.map(reaction => {
                const isMyReaction = reaction.users.some(
                  u => String(u.userId) === String(currentUserId)
                );
                return (
                  <button
                    key={reaction.emoji}
                    onClick={() => onReactionToggle?.(msg.id, reaction.emoji)}
                    className={`hit-44 inline-flex items-center gap-1 h-8 px-2.5 rounded-full text-xs border transition-colors ${
                      isMyReaction
                        ? 'bg-brand-50 border-brand-300 text-brand-700'
                        : darkMode
                          ? 'bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700'
                          : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                    }`}
                    title={reaction.users.map(u => u.userName).join(', ')}
                  >
                    <span>{reaction.emoji}</span>
                    <span>{reaction.count}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

/**
 * 메시지 한 건. 이 memo가 이 화면 성능의 핵심이다 —
 * 입력창 타이핑·타이핑 인디케이터·스크롤·업로드 진행률 같은 잦은 state 변경이
 * 목록 전체를 다시 그리지 않게 막는다. 새 prop을 추가할 때는 반드시 원시값이거나
 * useCallback/useMemo로 고정된 참조여야 한다.
 */
export const MessageItem = React.memo(MessageItemComponent);
